
import React from 'react';
import { BuildingOfficeIcon, StarIcon } from '@heroicons/react/24/outline';
import { StatusBadge } from '../../../../shared/components/badges/StatusBadge';
import SupplierManagmentExpandRow from '../components/SupplierManagmentExpandRow';
import TabbedForm from '../../../../shared/components/forms/FormTabs';
import { SupplierService } from '../../../../shared/services/supplierService';
import apiService from '../../../../shared/services/apiService';
import { API_ENDPOINTS } from '../../../../shared/services/api/endpoints';

/* ===============================
   COMMON OPTION LOADER
================================ */

const loadDropdownOptions = async (loaderFn, fallback) => {
  try {
    const res = await loaderFn();
    return res?.data || fallback;
  } catch {
    return fallback;
  }
};

export const getUserStatusOptions = () => loadDropdownOptions("userStatuses");

export const getRatingOptions = async () => Promise.resolve([
  { value: '5', label: '5 Stars' },
  { value: '4', label: '4 Stars' },
  { value: '3', label: '3 Stars' },
  { value: '2', label: '2 Stars' },
  { value: '1', label: '1 Star' }
]);



export const getAccountTypeOptions = async () => Promise.resolve([
  { value: 'CURRENT', label: 'Current' },
  { value: 'SAVINGS', label: 'Savings' },
  { value: 'BUSINESS', label: 'Business' }
]);

// Payment Terms from API
export const getPaymentTermsOptions = async () => {
  try {
    const response = await apiService.getAll(API_ENDPOINTS.DROPDOWNS.BASE);
    const data = response?.data?.paymentTerms ||  [];
   

    // Transform API response to dropdown format
    if (Array.isArray(data)) {
      return data.map(item => ({
        value: item.value || item.id || item,
        label: item.label || item.name || item
      }));
    }

    // Fallback options if API fails
    return [
      { value: 'NET_15', label: 'Net 15 Days' },
      { value: 'NET_30', label: 'Net 30 Days' },
      { value: 'NET_45', label: 'Net 45 Days' },
      { value: 'NET_60', label: 'Net 60 Days' },
      { value: 'IMMEDIATE', label: 'Immediate Payment' }
    ];
  } catch (error) {
    console.error('Failed to fetch payment terms:', error);
    // Fallback options
    return [
      { value: 'NET_15', label: 'Net 15 Days' },
      { value: 'NET_30', label: 'Net 30 Days' },
      { value: 'NET_45', label: 'Net 45 Days' },
      { value: 'NET_60', label: 'Net 60 Days' },
      { value: 'IMMEDIATE', label: 'Immediate Payment' }
    ];
  }
};

/* ===============================
   SUPPLIER CONFIG
================================ */

export const supplierManagementConfig = {
  title: 'Supplier',
  endpoint: 'admin/suppliers',
  storageKey: 'suppliers',
  icon: BuildingOfficeIcon,
  useUrlNavigation: true,
  basePath: '/admin/supplier-management',

  /* ========= TABLE COLUMNS ========= */
  columns: [
    { key: 'supplierName', label: 'Supplier', render: item => item.supplierName || '—' },
    {
      key: 'city',
      label: 'Location',
      render: item => `${item.city || ''}, ${item.state || ''}`
    },
    { key: 'fullName', label: 'Contact Person', render: item => item.fullName || '—' },
    { key: 'phone', label: 'Phone', render: item => item.phone || '—' },

    {
      key: 'status',
      label: 'Status',
      render: item => <StatusBadge status={item.status || 'ACTIVE'} />
    }
  ],

  /* ========= FORM FIELDS ========= */
  formFields: [
    /* BASIC */
    {
      name: 'supplierName',
      label: 'Supplier Name',
      type: 'text',
      required: true,
      section: 'basic',
      validate: (value) => {
        if (!value) return true;
        // Must contain at least one letter (not only numbers)
        return /[a-zA-Z]/.test(value);
      },
      validationMessage: 'Supplier Name must contain letters, not only numbers'
    },
    { name: 'supplierCode', label: 'Supplier Code', type: 'text', required: true, section: 'basic' },
    {
      name: 'fullName',
      label: 'Contact Person',
      type: 'text',
      required: true,
      section: 'basic',
      validate: (value) => {
        if (!value) return true;
        // Must contain only letters and spaces (no numbers)
        return /^[a-zA-Z\s]+$/.test(value);
      },
      validationMessage: 'Contact Person must contain only letters'
    },
    {
      name: 'phone',
      label: 'Phone',
      type: 'tel',
      required: true,
      section: 'basic',
      validate: (value) => {
        if (!value) return true;
        // Must be 10-15 digits only
        return /^[0-9]{10,15}$/.test(value);
      },
      validationMessage: 'Phone must be 10-15 digits only (no letters or special characters)'
    },
    {
      name: 'email',
      label: 'Email',
      type: 'email',
      required: true,
      section: 'basic',
      validate: (value) => {
        if (!value) return true;
        // Standard email validation
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      },
      validationMessage: 'Please enter a valid email address'
    },
    { name: 'website', label: 'Website', type: 'text', section: 'basic' },
    { name: 'rating', label: 'Rating', type: 'select', optionsLoader: getRatingOptions, default: '5', section: 'basic' },
    {
      name: 'isActive',
      label: 'Active Status',
      type: 'checkbox',
      default: true,
      section: 'basic',
      renderValue: (value) => value ? 'Active' : 'Inactive'
    },

    /* CONTACT */
    { name: 'address', label: 'Address', type: 'textarea', required: true, section: 'contact' },
    { name: 'location', label: 'Location',  },
    {
      name: 'city',
      label: 'City',
      type: 'text',
      required: true,
      section: 'contact',
      validate: (value) => {
        if (!value) return true;
        // Must contain only letters and spaces (no numbers)
        return /^[a-zA-Z\s]+$/.test(value);
      },
      validationMessage: 'City must contain only letters'
    },
    {
      name: 'state',
      label: 'State',
      type: 'text',
      required: true,
      section: 'contact',
      validate: (value) => {
        if (!value) return true;
        // Must contain only letters and spaces (no numbers)
        return /^[a-zA-Z\s]+$/.test(value);
      },
      validationMessage: 'State must contain only letters'
    },
    {
      name: 'country',
      label: 'Country',
      type: 'text',
      default: 'India',
      section: 'contact',
      validate: (value) => {
        if (!value) return true;
        // Must contain only letters and spaces (no numbers)
        return /^[a-zA-Z\s]+$/.test(value);
      },
      validationMessage: 'Country must contain only letters'
    },
    {
      name: 'pincode',
      label: 'Pincode',
      type: 'text',
      required: true,
      section: 'contact',
      validate: (value) => {
        if (!value) return true;
        // Must be 6 digits only (Indian pincode)
        return /^[0-9]{6}$/.test(value);
      },
      validationMessage: 'Pincode must be exactly 6 digits'
    },

    /* LEGAL & BANKING */
    {
      name: 'gstNumber',
      label: 'GST Number',
      type: 'text',
      required: true,
      section: 'legal',
      validate: (value) => {
        if (!value) return true;
        // Indian GST format: 15 alphanumeric characters
        return /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(value.toUpperCase());
      },
      validationMessage: 'Please enter a valid GST number (e.g., 22AAAAA0000A1Z5)'
    },
    {
      name: 'taxIdNumber',
      label: 'PAN / Tax ID',
      type: 'text',
      section: 'legal',
      validate: (value) => {
        if (!value) return true;
        // Indian PAN format: 10 alphanumeric characters
        return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value.toUpperCase());
      },
      validationMessage: 'Please enter a valid PAN number (e.g., ABCDE1234F)'
    },
    { name: 'licenseNumber', label: 'License Number', type: 'text', required: true, section: 'legal' },
    { name: 'licenseExpiryDate', label: 'License Expiry Date', type: 'date', section: 'legal' },
    {
      name: 'bankName',
      label: 'Bank Name',
      type: 'text',
      section: 'legal',
      validate: (value) => {
        if (!value) return true;
        // Must contain only letters and spaces (no numbers)
        return /^[a-zA-Z\s]+$/.test(value);
      },
      validationMessage: 'Bank Name must contain only letters'
    },
    {
      name: 'bankBranch',
      label: 'Bank Branch',
      type: 'text',
      section: 'legal',
      validate: (value) => {
        if (!value) return true;
        // Must contain at least one letter
        return /[a-zA-Z]/.test(value);
      },
      validationMessage: 'Bank Branch must contain letters'
    },
    { name: 'accountType', label: 'Account Type', type: 'select', optionsLoader: getAccountTypeOptions, section: 'legal' },
    {
      name: 'accountNumber',
      label: 'Account Number',
      type: 'text',
      section: 'legal',
      validate: (value) => {
        if (!value) return true;
        // Must be 9-18 digits only
        return /^[0-9]{9,18}$/.test(value);
      },
      validationMessage: 'Account Number must be 9-18 digits only'
    },
    {
      name: 'ifscCode',
      label: 'IFSC Code',
      type: 'text',
      section: 'legal',
      validate: (value) => {
        if (!value) return true;
        // Indian IFSC format: 4 letters + 0 + 6 alphanumeric
        return /^[A-Z]{4}0[A-Z0-9]{6}$/.test(value.toUpperCase());
      },
      validationMessage: 'Please enter a valid IFSC code (e.g., SBIN0001234)'
    },

    /* FINANCIAL */
    { name: 'paymentTerms', label: 'Payment Terms', type: 'select', optionsLoader: getPaymentTermsOptions, default: 'NET_30', section: 'financial' },
    { name: 'creditLimit', label: 'Credit Limit (₹)', type: 'number', default: 0, section: 'financial' },
    { name: 'totalOrderValue', label: 'Total Order Value (₹)', type: 'number', default: 0, section: 'financial' },
    { name: 'notes', label: 'Notes', type: 'textarea', section: 'financial' },

    /* VACCINE */
    { name: 'vaccineType', label: 'Vaccine Type', type: 'text', section: 'vaccine' },
    { name: 'vaccineSupplied', label: 'Vaccine Supplied', type: 'text', section: 'vaccine' },
    { name: 'temperature', label: 'Storage Temperature', type: 'text', section: 'vaccine' },
    { name: 'maxSupplyCapacity', label: 'Max Supply Capacity', type: 'text', section: 'vaccine' },
    { name: 'certification', label: 'Certification', type: 'text', section: 'vaccine' },
    { name: 'expiredStockHandling', label: 'Expired Stock Handling', type: 'textarea', section: 'vaccine' }
  ],

  searchFields: ['supplierName', 'supplierCode', 'fullName', 'phone', 'email', 'city'],


  formState: { activeTab: 'basic' },

  customHandlers: {
    onView: async (item) => {
      const response = await SupplierService.getById(item.id || item._id);
      return response?.data || response;
    },
    renderFormSections: (formFields, formData, handleFormChange, mode, renderFormField, activeTab, setActiveTab) => {
      const tabsConfig = {
          basic: { label: 'Basic' },
           contact: { label: 'Contact' },
          legal: { label: 'Legal & Banking' },
           financial: { label: 'Financial' },
          vaccine: { label: 'Vaccine' }
      };
      return <TabbedForm formFields={formFields} formData={formData} handleFormChange={handleFormChange} renderFormField={renderFormField} tabsConfig={tabsConfig} activeTab={activeTab} setActiveTab={setActiveTab} />;
    },

    renderExpandedRow: item => <SupplierManagmentExpandRow item={item} />,

    processFormData: (formData) => ({
      ...formData,
      rating: String(formData.rating),
     creditLimit: Number(formData.creditLimit) || 0,
     totalOrderValue: Number(formData.totalOrderValue) || 0,
      // totalOrderValue: String(formData.totalOrderValue || 0),
   lastOrderDate: new Date().toISOString()
    })
  }
};
